import type { Book, FolderRef } from '../types';
import { naturalCompare } from './format';

/**
 * Folds the flat book list back into the folders it came from, one level at a
 * time, so a Drive folder holding a hundred books shows as one tile rather
 * than a hundred.
 *
 * A folder holding a single book is not worth a tap: that book is shown in the
 * folder's place. This keeps the common /Author/Title/ layout flat instead of
 * turning every author into a one-book folder.
 */

export type ShelfEntry =
  /** `folder` is set only in search results, naming the folder the book is
   *  normally found in when that is not the folder being searched. */
  | { kind: 'book'; book: Book; folder?: FolderRef }
  | { kind: 'collection'; folder: FolderRef; books: Book[] };

export interface Shelf {
  /** Folders from the root down to this one; empty for the root itself. */
  trail: FolderRef[];
  entries: ShelfEntry[];
}

/**
 * The contents of one folder, or the root when `folderId` is null. Returns null
 * when no book in the library lives under that folder any more.
 */
export function shelfAt(books: Book[], folderId: string | null): Shelf | null {
  const trail = folderId === null ? [] : trailTo(books, folderId);
  if (!trail) return null;

  const loose: Book[] = [];
  const groups = new Map<string, { folder: FolderRef; books: Book[] }>();

  for (const book of books) {
    const path = book.folderPath ?? [];
    if (!startsWith(path, trail)) continue;

    const next = path[trail.length];
    if (!next) {
      loose.push(book);
      continue;
    }
    const group = groups.get(next.id);
    if (group) group.books.push(book);
    else groups.set(next.id, { folder: next, books: [book] });
  }

  const collections: ShelfEntry[] = [];
  for (const group of groups.values()) {
    if (group.books.length === 1 && group.books[0]) loose.push(group.books[0]);
    else collections.push({ kind: 'collection', ...group });
  }

  collections.sort((a, b) => naturalCompare(entryName(a), entryName(b)));
  loose.sort((a, b) => naturalCompare(a.title, b.title));

  // Folders first, as in a file manager: they are what you navigate through.
  return { trail, entries: [...collections, ...loose.map((book) => ({ kind: 'book' as const, book }))] };
}

/**
 * The folder whose shelf actually shows this book — its parent, unless that
 * parent was collapsed away for holding only this one book. Null means the
 * root. Used to send "back" from the player to where the book was opened.
 */
export function shelfFolderOf(books: Book[], book: Book): string | null {
  return displayFolder(book, folderSizes(books))?.id ?? null;
}

/**
 * Books and folders under `folderId` (the whole library when null) whose names
 * contain every word of `query`, ignoring case and accents. Books match on
 * title and author, folders on their name. Names that start with the query
 * come first. Returns null when the folder no longer exists.
 */
export function searchShelf(books: Book[], folderId: string | null, query: string): Shelf | null {
  const trail = folderId === null ? [] : trailTo(books, folderId);
  if (!trail) return null;

  const terms = normalize(query).split(/\s+/).filter(Boolean);
  const whole = terms.join(' ');
  const sizes = folderSizes(books);
  const inScope = books.filter((b) => startsWith(b.folderPath ?? [], trail));

  const folders = new Map<string, FolderRef>();
  for (const book of inScope) {
    for (const f of (book.folderPath ?? []).slice(trail.length)) folders.set(f.id, f);
  }

  // One-book folders are never shown as folders, so they are not results
  // either; their book is found by its own title and author instead.
  const folderHits = [...folders.values()]
    .filter((f) => (sizes.get(f.id) ?? 0) > 1 && matchesAll(f.name, terms))
    .map((folder) => ({
      rank: rank(folder.name, whole, terms),
      entry: {
        kind: 'collection' as const,
        folder,
        books: inScope.filter((b) => b.folderPath?.some((p) => p.id === folder.id)),
      },
    }));

  const bookHits = inScope
    .filter((book) => matchesAll(searchableText(book, sizes), terms))
    .map((book) => {
      const where = displayFolder(book, sizes);
      return {
        rank: rank(book.title, whole, terms),
        entry: {
          kind: 'book' as const,
          book,
          ...(where && where.id !== folderId ? { folder: where } : {}),
        },
      };
    });

  const byRank = (a: { rank: number; entry: ShelfEntry }, b: { rank: number; entry: ShelfEntry }) =>
    a.rank - b.rank || naturalCompare(entryName(a.entry), entryName(b.entry));

  return {
    trail,
    entries: [...folderHits.sort(byRank), ...bookHits.sort(byRank)].map((hit) => hit.entry),
  };
}

/** Lower-cased with accents removed, so "bronte" finds "Brontë". */
function normalize(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

function matchesAll(text: string, terms: string[]): boolean {
  const haystack = normalize(text);
  return terms.length > 0 && terms.every((t) => haystack.includes(t));
}

/** 0: the name starts with the query; 1: every word starts a word in the
 *  name; 2: the words only appear somewhere inside it. */
function rank(name: string, whole: string, terms: string[]): number {
  const n = normalize(name);
  if (n.startsWith(whole)) return 0;
  const words = n.split(/[^\p{L}\p{N}]+/u);
  return terms.every((t) => words.some((w) => w.startsWith(t))) ? 1 : 2;
}

/**
 * Title and author, except where the "author" is only the name of the folder
 * the book sits in (the scanner's guess) and that folder is shown as a folder.
 * Matching on it would list every book in "Top 100 books" when you search for
 * "top", next to the folder itself.
 */
function searchableText(book: Book, sizes: Map<string, number>): string {
  const parent = book.folderPath?.[book.folderPath.length - 1];
  const authorIsFolder =
    parent !== undefined && book.author === parent.name && (sizes.get(parent.id) ?? 0) > 1;
  return authorIsFolder || !book.author ? book.title : `${book.title} ${book.author}`;
}

/** How many books sit anywhere under each folder. */
function folderSizes(books: Book[]): Map<string, number> {
  const sizes = new Map<string, number>();
  for (const book of books) {
    for (const f of book.folderPath ?? []) sizes.set(f.id, (sizes.get(f.id) ?? 0) + 1);
  }
  return sizes;
}

/** The folder whose shelf shows this book: the outermost folder that holds
 *  only this book is collapsed away, along with everything inside it. */
function displayFolder(book: Book, sizes: Map<string, number>): FolderRef | null {
  const path = book.folderPath ?? [];
  for (let depth = 0; depth < path.length; depth += 1) {
    const folder = path[depth];
    if (folder && (sizes.get(folder.id) ?? 0) <= 1) return path[depth - 1] ?? null;
  }
  return path[path.length - 1] ?? null;
}

function trailTo(books: Book[], folderId: string): FolderRef[] | null {
  for (const book of books) {
    const path = book.folderPath ?? [];
    const at = path.findIndex((f) => f.id === folderId);
    if (at !== -1) return path.slice(0, at + 1);
  }
  return null;
}

function startsWith(path: FolderRef[], prefix: FolderRef[]): boolean {
  if (path.length < prefix.length) return false;
  return prefix.every((f, i) => path[i]?.id === f.id);
}

function entryName(entry: ShelfEntry): string {
  return entry.kind === 'collection' ? entry.folder.name : entry.book.title;
}
